import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import { getStandInfo } from '../../lib/conventionResults';
import { getStandMagnumInfo, formatStandMagnumSummary } from '../../lib/conventionCourse';

@observer
class ConventionViewStageT96 extends Component {
    renderEditableCell({ participant, stage, stand, name, value, max, isAllowToEditScore, addRef, updateResult, handleFocus, targetResultId }) {
        return (
            <input
                type='tel'
                pattern='[0-9]*'
                inputmode='numeric'
                min='0'
                max={max}
                data-delay={300}
                class={`form-control form-control-sm py-0 px-1 text-center ${(value > max) ? 'bg-danger-light' : ''}`}
                style='width: 44px; min-width: 44px; max-width: 44px; font-size: 1rem;'
                size='2'
                data-participant={participant.id}
                data-stand={stand}
                data-id={targetResultId}
                data-stage={stage.md5}
                data-type={stage.type}
                name={name}
                ref={c => addRef(`${participant.id}-${stage.md5}-${stand}-${name}`, c)}
                onFocus={handleFocus}
                onInput={updateResult}
                value={value >= 0 ? value : ''}
                disabled={!isAllowToEditScore}
                autoComplete='off'
            />
        );
    }

    renderParticipantBlock(participant, participantResults, stage) {
        const { stores, idx, editParticipant } = this.props;
        const { conventionResultStore, conventionStore, userStore } = stores;
        const { convention } = conventionStore;
        const { user = {}, isAdmin } = userStore;
        const isYou = participant.id === user.id;
        const isAllowedToEditSignup = (isAdmin || isYou) && !!editParticipant;
        const series = conventionResultStore.getT96Series();
        const groups = conventionResultStore.getT96DistanceGroups();

        const distanceTextId = (d) => `convention.t96-distance-${d}`;
        const distanceLabelDefault = (d) => d.replace('m', ' m');

        const gripLabel = (grip) => grip === '1 hånd'
            ? <Text id='convention.t96-grip-1-hand'>1 hånd</Text>
            : <Text id='convention.t96-grip-fri'>Fri</Text>;

        const seriesByIdx = (idx) => series.find(s => s.idx === idx);
        const resultByStand = (stand) => participantResults.find(r => r.stand === stand);

        let grandTotal = 0;
        let grandIx = 0;

        const groupRows = groups.map(group => {
            const rows = [];
            let sumHits = 0;
            let sumTargets = 0;
            let sumInner = 0;
            let sumTotal = 0;

            for (let stand = group.startIdx; stand <= group.endIdx; stand += 1) {
                const s = seriesByIdx(stand);
                const r = resultByStand(stand);
                const hits    = r?.scoreHits    || 0;
                const targets = r?.scoreTargets || 0;
                const inner   = r?.scoreInner   || 0;
                const total   = hits + targets + inner;
                sumHits    += hits;
                sumTargets += targets;
                sumInner   += inner;
                sumTotal   += total;

                const editable = this.props.isAllowedToEdit;
                const cell = (name, value, max) => editable
                    ? this.renderEditableCell({
                        participant, stage, stand, name, value, max,
                        isAllowToEditScore: this.props.isAllowToEditScore,
                        addRef: this.props.addRef,
                        updateResult: this.props.updateResult,
                        handleFocus: this.props.handleFocus,
                        targetResultId: r?.id,
                    })
                    : (value > 0 ? value : <span class='text-body-tertiary'>—</span>);

                rows.push(
                    <tr key={`${participant.id}-${stand}`}>
                        <td class='py-1 px-2 text-body-secondary' style='font-size: 0.85rem;'>
                            {s.timing} {gripLabel(s.grip)}
                            {(() => {
                                const info = getStandInfo(convention, stand);
                                const magnum = getStandMagnumInfo(convention, stand);
                                if (!info && !magnum) return null;
                                return (
                                    <span class='ms-1 text-body-tertiary' style='font-size: 0.75rem;'>
                                        {info?.shootingPosition && <span class='ms-1'>· {info.shootingPosition}</span>}
                                        {info?.shootingTime != null && <span class='ms-1'>· {info.shootingTime}s</span>}
                                        {magnum && (
                                            <span class='ms-1'>
                                                · <Text id='convention.course-magnum'>Magnum</Text>: {formatStandMagnumSummary(magnum)}
                                            </span>
                                        )}
                                        {info?.notes && <i class='fa-duotone fa-note ms-1' title={info.notes} />}
                                    </span>
                                );
                            })()}
                        </td>
                        <td class='py-1 px-2 text-center'>{cell('scoreHits',    hits,    6)}</td>
                        <td class='py-1 px-2 text-center'>{cell('scoreTargets', targets, 5)}</td>
                        <td class='py-1 px-2 text-center'>{cell('scoreInner',   inner,   6)}</td>
                        <td class='py-1 px-2 text-center text-body-secondary' style='font-size: 0.85rem;'>
                            {total > 0 ? total : ''}
                        </td>
                    </tr>
                );
            }
            grandTotal += sumTotal;
            grandIx    += sumInner;

            return (
                <tbody key={group.distance}>
                    <tr>
                        <th colspan='5' class='py-1 px-2 text-body fw-semibold' style='font-size: 0.85rem;'>
                            <Text id={distanceTextId(group.distance)}>{distanceLabelDefault(group.distance)}</Text>
                        </th>
                    </tr>
                    {rows}
                    <tr class='border-top'>
                        <td class='py-1 px-2 fw-semibold' style='font-size: 0.85rem;'>
                            <Text id='convention.t96-sum-distance' fields={{ distance: distanceLabelDefault(group.distance) }}>
                                Sum {distanceLabelDefault(group.distance)}
                            </Text>
                        </td>
                        <td class='py-1 px-2 text-center fw-semibold'>{sumHits}</td>
                        <td class='py-1 px-2 text-center fw-semibold'>{sumTargets}</td>
                        <td class='py-1 px-2 text-center fw-semibold'>{sumInner}</td>
                        <td class='py-1 px-2 text-center fw-bold'>{sumTotal}</td>
                    </tr>
                </tbody>
            );
        });

        return (
            <div class='mb-3' key={participant.id}>
                <div
                    class='d-flex align-items-center mb-1'
                    style={`font-size: 0.95rem; ${isAllowedToEditSignup ? 'cursor: pointer;' : ''}`}
                    data-id={convention.id}
                    data-md5={participant.md5}
                    data-stage={stage.md5}
                    data-idx={idx}
                    onClick={isAllowedToEditSignup ? editParticipant : undefined}
                    title={isAllowedToEditSignup ? 'Endre klasse' : undefined}
                >
                    {isAllowedToEditSignup && (
                        <i class='fa-solid fa-edit me-1 text-body-secondary' style='font-size: 0.7rem;' />
                    )}
                    <span class='fw-semibold'>{localUtil.displayName(participant)}</span>
                    <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>{participant.class || '?'}</span>
                </div>
                <div class='table-responsive'>
                    <table class='table table-sm mb-0' style='font-size: 0.85rem;'>
                        <thead>
                            <tr class='text-body-secondary' style='font-size: 0.75rem; font-weight: 400;'>
                                <th>&nbsp;</th>
                                <th class='text-center'><Text id='convention.t96-column-treff'>Treff</Text></th>
                                <th class='text-center'><Text id='convention.t96-column-fig'>Fig</Text></th>
                                <th class='text-center'><Text id='convention.t96-column-inner'>Inner</Text></th>
                                <th class='text-center'>Sum</th>
                            </tr>
                        </thead>
                        {groupRows}
                        <tfoot>
                            <tr class='border-top'>
                                <td colspan='4' class='py-1 px-2 text-end fw-semibold'>
                                    <Text id='convention.t96-resultat'>Resultat</Text>
                                </td>
                                <td class='py-1 px-2 text-center fw-bold' style='font-size: 1.0rem;'>
                                    {grandTotal} <span class='text-body-tertiary fw-light' style='font-size: 0.7rem;'>/ 272</span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    }

    render() {
        const { stage, participants = [], stores } = this.props;
        const { conventionStore, conventionResultStore } = stores;
        const { convention } = conventionStore;
        const conventionResults = conventionResultStore.conventionResults;

        return (
            <div class='d-flex flex-column'>
                {participants.map(participant => {
                    const participantResults = conventionResults.filter(r =>
                        r.participant === participant.id
                        && r.stage === stage.md5
                        && r.convention === convention.id
                    );
                    return this.renderParticipantBlock(participant, participantResults, stage);
                })}
            </div>
        );
    }
}

export default ConventionViewStageT96;
